import {
  Mesh,
  Vector3,
  Quaternion,
  StandardMaterial,
  Color3,
  PhysicsAggregate,
  PhysicsShapeType,
  PhysicsMotionType,
  VertexData,
  type Scene,
} from '@babylonjs/core';
import type { FlipperDef } from '../core/types';

interface Flipper {
  mesh: Mesh;
  aggregate: PhysicsAggregate | null;
  def: FlipperDef;
  currentAngle: number;
  targetAngle: number;
  hinge: Vector3;       // World hinge position (fixed pivot point)
  centerOffsetX: number; // Hinge -> center offset along local X
  restAngle: number;    // Rest angle for this flipper (sign depends on side)
  activeAngle: number;  // Active angle for this flipper (sign depends on side)
}

/**
 * Kinematic flipper controller.
 * Flippers rotate on button press, pushing the ball.
 *
 * Key insight: Don't use setPivotPoint with physics - it causes Havok
 * to misinterpret the shape. Instead, keep mesh centered and compute
 * position from hinge + angle.
 */
export class FlipperController {
  private scene: Scene;
  private flippers: Map<string, Flipper> = new Map();

  // Tuning
  readonly restAngle = 0.4;   // Radians, angled down toward drain
  readonly activeAngle = -0.5; // Radians, angled up to hit ball
  readonly flipSpeed = 20;     // Radians per second (activation)
  readonly returnSpeed = 8;    // Slower return

  constructor(scene: Scene) {
    this.scene = scene;
  }

  /** Compute mesh center position from hinge, offset, and rotation angle */
  private computeCenterPos(hinge: Vector3, centerOffsetX: number, yaw: number): Vector3 {
    // Rotate (centerOffsetX, 0, 0) around Y by yaw, then add hinge
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    return new Vector3(
      hinge.x + centerOffsetX * c,
      hinge.y,
      hinge.z - centerOffsetX * s  // Minus sign for correct rotation direction
    );
  }

  /** Create a wedge-shaped mesh for the flipper */
  private createWedgeMesh(name: string, length: number, side: 'left' | 'right'): Mesh {
    const mesh = new Mesh(name, this.scene);

    // Wedge dimensions
    const hingeWidth = 0.3;   // Wide end at pivot
    const tipWidth = 0.12;    // Narrow end at tip
    const height = 0.12;      // Flipper thickness
    const halfLen = length / 2;
    const hh = height / 2;

    // Left flipper: tip (narrow) at -X local, hinge (wide) at +X local
    // Right flipper: tip (narrow) at +X local, hinge (wide) at -X local
    const negXWidth = side === 'right' ? hingeWidth : tipWidth;
    const posXWidth = side === 'right' ? tipWidth : hingeWidth;

    // 8 vertices for a tapered box
    const positions = [
      // End at x = -halfLen
      -halfLen, -hh, -negXWidth / 2,  // 0
      -halfLen, -hh,  negXWidth / 2,  // 1
      -halfLen,  hh,  negXWidth / 2,  // 2
      -halfLen,  hh, -negXWidth / 2,  // 3
      // End at x = +halfLen
       halfLen, -hh, -posXWidth / 2,  // 4
       halfLen, -hh,  posXWidth / 2,  // 5
       halfLen,  hh,  posXWidth / 2,  // 6
       halfLen,  hh, -posXWidth / 2,  // 7
    ];

    // 12 triangles (2 per face, 6 faces)
    const indices = [
      // Left face (hinge end)
      0, 2, 1,  0, 3, 2,
      // Right face (tip end)
      4, 5, 6,  4, 6, 7,
      // Top face
      3, 7, 6,  3, 6, 2,
      // Bottom face
      0, 1, 5,  0, 5, 4,
      // Front face
      1, 2, 6,  1, 6, 5,
      // Back face
      0, 4, 7,  0, 7, 3,
    ];

    // Simple normals (not smooth-shaded, but fine for this)
    const normals: number[] = [];
    VertexData.ComputeNormals(positions, indices, normals);

    const vertexData = new VertexData();
    vertexData.positions = positions;
    vertexData.indices = indices;
    vertexData.normals = normals;
    vertexData.applyToMesh(mesh);

    return mesh;
  }

  /** Create a flipper mesh and physics */
  createFlipper(def: FlipperDef, physicsEnabled: boolean): Mesh {
    const mesh = this.createWedgeMesh(def.id, def.length, def.side);

    // Hinge in world space (the fixed pivot point)
    const hinge = new Vector3(def.position.x, def.position.y, def.position.z);

    // Center offset from hinge (pivot point):
    // - Left flipper: wide end at +X local, center = pivot - halfLen (negative offset)
    // - Right flipper: wide end at -X local, center = pivot + halfLen (positive offset)
    const centerOffsetX = def.side === 'left' ? -def.length / 2 : def.length / 2;

    // Rest angles: tips point inward forming a V shape (toward -Z / back of table)
    // - Left flipper: positive angle rotates tip (-X local) toward -Z
    // - Right flipper: negative angle rotates tip (+X local) toward -Z
    const restAngle = def.side === 'left' ? this.restAngle : -this.restAngle;
    const q = Quaternion.RotationYawPitchRoll(restAngle, 0, 0);
    const centerPos = this.computeCenterPos(hinge, centerOffsetX, restAngle);

    mesh.position.copyFrom(centerPos);
    mesh.rotationQuaternion = q;

    // Material
    const mat = new StandardMaterial(`${def.id}-mat`, this.scene);
    mat.diffuseColor = new Color3(0.8, 0.2, 0.2); // Red flippers
    mat.specularColor = new Color3(0.3, 0.3, 0.3);
    mesh.material = mat;

    // Physics (kinematic - we control position, physics handles collisions)
    let aggregate: PhysicsAggregate | null = null;
    if (physicsEnabled) {
      aggregate = new PhysicsAggregate(
        mesh,
        PhysicsShapeType.BOX,
        {
          mass: 1,          // Use mass=1 to avoid edge cases with ANIMATED + mass=0
          friction: 0.4,
          restitution: 0.3,
        },
        this.scene
      );
      aggregate.body.setMotionType(PhysicsMotionType.ANIMATED);

      // IMPORTANT: Seed the target transform immediately so the first physics
      // step doesn't "correct" it from a default/stale transform
      aggregate.body.setTargetTransform(
        mesh.absolutePosition,
        mesh.absoluteRotationQuaternion
      );
    }

    // Active angles: tips swing down toward drain (+Z) when pressed
    const activeAngle = def.side === 'left' ? this.activeAngle : -this.activeAngle;

    this.flippers.set(def.id, {
      mesh,
      aggregate,
      def,
      currentAngle: restAngle,
      targetAngle: restAngle,
      hinge,
      centerOffsetX,
      restAngle,
      activeAngle,
    });

    return mesh;
  }

  /** Set flipper state (pressed or not) */
  setFlipperState(side: 'left' | 'right', pressed: boolean): void {
    for (const flipper of this.flippers.values()) {
      if (flipper.def.side !== side) continue;
      flipper.targetAngle = pressed ? flipper.activeAngle : flipper.restAngle;
    }
  }

  /** Update flipper positions (call each fixed step) */
  update(dt: number): void {
    for (const flipper of this.flippers.values()) {
      const diff = flipper.targetAngle - flipper.currentAngle;

      if (Math.abs(diff) < 0.01) {
        flipper.currentAngle = flipper.targetAngle;
      } else {
        // FIX: Pick speed based on whether we're going to active or rest,
        // not based on the sign of diff (which varies by flipper side)
        const goingToActive = Math.abs(flipper.targetAngle - flipper.activeAngle) < 0.01;
        const speed = goingToActive ? this.flipSpeed : this.returnSpeed;
        const sign = Math.sign(diff);
        flipper.currentAngle += sign * speed * dt;

        // Clamp to not overshoot
        if (sign > 0 && flipper.currentAngle > flipper.targetAngle) {
          flipper.currentAngle = flipper.targetAngle;
        }
        if (sign < 0 && flipper.currentAngle < flipper.targetAngle) {
          flipper.currentAngle = flipper.targetAngle;
        }
      }

      // Update mesh position and rotation
      const q = Quaternion.RotationYawPitchRoll(flipper.currentAngle, 0, 0);
      const centerPos = this.computeCenterPos(
        flipper.hinge,
        flipper.centerOffsetX,
        flipper.currentAngle
      );

      flipper.mesh.position.copyFrom(centerPos);
      flipper.mesh.rotationQuaternion = q;

      // Always update physics transform to keep body in sync
      if (flipper.aggregate) {
        flipper.aggregate.body.setTargetTransform(
          flipper.mesh.absolutePosition,
          flipper.mesh.absoluteRotationQuaternion
        );
      }
    }
  }

  dispose(): void {
    for (const flipper of this.flippers.values()) {
      flipper.aggregate?.dispose();
      flipper.mesh.dispose();
    }
    this.flippers.clear();
  }
}
